import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shell } from "@/components/Shell";
import { api } from "@/lib/api";
import { SEOHead, JsonLd } from "@/components/SEO";
import { Calendar, MapPin, ArrowRight } from "lucide-react";

/**
 * /health-fairs — index of every city market that hosts health fairs.
 *
 * Owns the "health fairs near me" search intent at the national level.
 * Each card links to /health-fairs/{slug}. JSON-LD: CollectionPage.
 */
export default function HealthFairsIndex() {
    const [markets, setMarkets] = useState(null);

    useEffect(() => {
        (async () => {
            const r = await api.get("/seo/health-fairs");
            setMarkets(r.data.markets || []);
        })();
    }, []);

    const totalUpcoming = (markets || []).reduce((s, m) => s + (m.upcoming_count || 0), 0);
    const description = `Find corporate health fairs by city. HealthFairX is currently tracking ${markets ? markets.length : "—"} active metros and ${totalUpcoming} upcoming employee health fairs.`;

    return (
        <Shell>
            <SEOHead title="Corporate health fairs by city" description={description} />
            <JsonLd data={{
                "@context": "https://schema.org",
                "@type": "CollectionPage",
                name: "Corporate health fairs by city",
                description,
            }} />

            <section className="bg-brand-bone border-b border-brand-line">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
                    <div className="label-base">HealthFairX markets</div>
                    <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight mt-2" data-testid="hf-index-h1">
                        Corporate health fairs near you
                    </h1>
                    <p className="mt-3 text-brand-mute max-w-3xl">
                        Browse upcoming and past employee health fairs by city. Find the metros where HealthFairX is most active.
                    </p>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
                {!markets && <div className="p-10 text-brand-mute">Loading…</div>}
                {markets && markets.length === 0 && (
                    <div className="rounded-xl border border-dashed border-brand-line p-12 text-center text-brand-mute">
                        No health fair markets are listed yet.
                    </div>
                )}
                {markets && markets.length > 0 && (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="hf-market-grid">
                        {markets.map((m) => (
                            <Link key={m.slug} to={`/health-fairs/${m.slug}`}
                                  data-testid={`hf-market-${m.slug}`}
                                  className="rounded-2xl border border-brand-line bg-white p-6 card-hover block">
                                <div className="flex items-center gap-2 text-brand-forest font-bold">
                                    <MapPin size={18} /> {m.city}{m.state ? `, ${m.state}` : ""}
                                </div>
                                <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                                    <div className="rounded-lg bg-brand-mint/40 p-3">
                                        <div className="text-[10px] uppercase tracking-wider font-bold text-brand-forest">Upcoming</div>
                                        <div className="font-heading text-2xl font-extrabold text-brand-forest">{m.upcoming_count}</div>
                                    </div>
                                    <div className="rounded-lg bg-brand-bone p-3">
                                        <div className="text-[10px] uppercase tracking-wider font-bold text-brand-mute">Past</div>
                                        <div className="font-heading text-2xl font-extrabold text-brand-mute">{m.past_count}</div>
                                    </div>
                                </div>
                                <div className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-brand-coral">
                                    View market <ArrowRight size={14} />
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </Shell>
    );
}
